import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useSelectedGamesData } from "entities/modules/stores/useSelectedGamesData";
import { useTagBlocksStore } from "entities/modules/stores/useTagBlocksStore";
import { sumBy } from "lodash";
import React, { FC, SyntheticEvent } from "react";
import { COLOR_PRIMARY_MAIN } from "shared/theme/colors";
import { useShallow } from "zustand/react/shallow";
import { useContentScheduleModuleDetails } from "../../context";
import { ModuleDetailsTabs } from "../../context/types";
import { useGeneralData } from "../../stores/useGeneralData";
import { useStudioOrderingData } from "../../stores/useStudioOrderingData";
import TabWithIndicator from "./components/TabWithIndicator";
import { StyledTab, StyledTabs } from "./styled";
import { getTabProps, getTabsByModuleType } from "./utils";

const Tabs: FC = () => {
  const { selectedTab, setSelectedTab, isFetchingSelectedGamesList } =
    useContentScheduleModuleDetails();
  const { allowed: hasReadAccess } = useRolePermission("brandsAndPages:read");
  const { allowed: hasReadTranslationsAccess } = useRolePermission(
    "brandsAndPages:translations:read",
  );

  const shouldRenderAllTabs = hasReadAccess;
  const shouldRenderTranslationsTab =
    shouldRenderAllTabs || hasReadTranslationsAccess;

  const { moduleType, studioFilterEnabled } = useGeneralData(
    useShallow((state) => ({
      moduleType: state.moduleType,
      studioFilterEnabled: state.studioFilterEnabled,
    })),
  );
  const tagBlocks = useTagBlocksStore((state) => state.tagBlocks);
  const selectedGames = useSelectedGamesData((state) => state.selectedGames);
  const orderedStudios = useStudioOrderingData((state) => state.orderedStudios);

  const assignedTagCount = sumBy(
    tagBlocks,
    (block) => block.selectedTags.length,
  );

  const additionalTabs = moduleType
    ? getTabsByModuleType({
        moduleType: moduleType,
        studioFilterEnabled,
      })
    : [];

  const handleTabChange = (_: SyntheticEvent, newTab: ModuleDetailsTabs) => {
    setSelectedTab(newTab);
  };

  return (
    <StyledTabs
      value={selectedTab}
      onChange={handleTabChange}
      TabIndicatorProps={{
        style: {
          backgroundColor: COLOR_PRIMARY_MAIN,
          height: 2,
        },
      }}
    >
      {shouldRenderAllTabs && (
        <StyledTab
          {...getTabProps(ModuleDetailsTabs.General)}
          disableRipple
          label="General"
          value={ModuleDetailsTabs.General}
        />
      )}
      {shouldRenderAllTabs &&
        additionalTabs.map((tab, index) => {
          const indicatorShown =
            (tab.id === ModuleDetailsTabs.Tags && !!assignedTagCount) ||
            (tab.id === ModuleDetailsTabs.SelectedGames &&
              !!selectedGames.length) ||
            (tab.id === ModuleDetailsTabs.StudioOrdering &&
              !!orderedStudios.length);

          const indicatorCount = {
            [ModuleDetailsTabs.General]: 0,
            [ModuleDetailsTabs.Tags]: assignedTagCount,
            [ModuleDetailsTabs.SelectedGames]: selectedGames.length,
            [ModuleDetailsTabs.StudioOrdering]: selectedGames.length,
            [ModuleDetailsTabs.Studios]: 0,
            [ModuleDetailsTabs.Translations]: 0,
          }[tab.id];

          const indicatorLoading = {
            [ModuleDetailsTabs.General]: false,
            [ModuleDetailsTabs.Tags]: false,
            [ModuleDetailsTabs.SelectedGames]: isFetchingSelectedGamesList,
            [ModuleDetailsTabs.StudioOrdering]: false,
            [ModuleDetailsTabs.Studios]: false,
            [ModuleDetailsTabs.Translations]: false,
          }[tab.id];

          return (
            <StyledTab
              {...getTabProps(tab.id)}
              key={index}
              value={tab.id}
              disableRipple
              label={
                <TabWithIndicator
                  label={tab.label}
                  indicatorLoading={indicatorLoading}
                  indicatorShown={indicatorShown}
                  indicatorCount={indicatorCount}
                />
              }
            />
          );
        })}
      {shouldRenderTranslationsTab && (
        <StyledTab
          {...getTabProps(ModuleDetailsTabs.Translations)}
          value={ModuleDetailsTabs.Translations}
          disableRipple
          label="Translations"
        />
      )}
    </StyledTabs>
  );
};

export default Tabs;
